import {
  PUZZLE_ATTACK_BUBBLES_PER_ROW,
  PUZZLE_BOARD_COLUMNS,
  PUZZLE_BOARD_HEIGHT,
  PUZZLE_BOARD_ROWS,
  PUZZLE_BOARD_WIDTH,
  PUZZLE_BUBBLE_RADIUS,
  PUZZLE_COLORS,
  PUZZLE_COUNTDOWN_TICKS,
  PUZZLE_DANGER_LINE_Y,
  PUZZLE_DROP_POINTS_PER_BUBBLE,
  PUZZLE_EMPTY_INPUT,
  PUZZLE_FIXED_DELTA_SECONDS,
  PUZZLE_INITIAL_ROWS,
  PUZZLE_MATCH_POINTS_PER_BUBBLE,
  PUZZLE_MAX_AIM,
  PUZZLE_MAX_ATTACK_ROWS,
  PUZZLE_MAX_PENDING_ATTACK_BUBBLES,
  PUZZLE_MAX_SHOT_TICKS,
  PUZZLE_MIN_AIM,
  PUZZLE_MISSES_PER_PRESSURE,
  PUZZLE_OVERTIME_PRESSURE_INTERVAL_TICKS,
  PUZZLE_PROJECTILE_SPEED,
  PUZZLE_ROUND_END_TICKS,
  PUZZLE_ROUNDS_TO_WIN,
  PUZZLE_ROUND_TICKS,
  PUZZLE_SHOOTER_X,
  PUZZLE_SHOOTER_Y,
  PUZZLE_SHOT_SUBSTEPS,
} from "./constants";
import {
  getPuzzleLegalSnapCells,
  getPuzzleNeighborPositions,
  puzzleCellKey,
  puzzleCellToPoint,
  sortPuzzleCells,
  type PuzzleGridPosition,
} from "./grid";
import type {
  CreatePuzzleMatchOptions,
  PuzzleBoardState,
  PuzzleBotDifficulty,
  PuzzleBubbleKind,
  PuzzleCell,
  PuzzleColor,
  PuzzleEvent,
  PuzzleInput,
  PuzzleInputsByPlayer,
  PuzzleLaunchBubble,
  PuzzleMatchState,
  PuzzlePlayerSlot,
  PuzzlePressureSource,
  PuzzleTeamId,
} from "./types";

interface RandomStep {
  state: number;
  unit: number;
}

interface ShotImpact {
  playerId: string;
  shotId: number;
  x: number;
  y: number;
  collisionCell: PuzzleGridPosition | null;
  ceiling: boolean;
}

interface ResolutionResult {
  playerId: string;
  blocked: boolean;
  cleared: boolean;
  missPressureRows: number;
}

const PUZZLE_BUBBLE_KINDS: ReadonlyArray<PuzzleBubbleKind> = Object.freeze([
  "normal",
  "color_burst",
  "row_burst",
  "column_burst",
]);

const PUZZLE_BOT_DIFFICULTIES: ReadonlyArray<PuzzleBotDifficulty> =
  Object.freeze(["easy", "medium", "hard", "superhuman", "god"]);

function isPuzzleBubbleKind(value: unknown): value is PuzzleBubbleKind {
  return (
    typeof value === "string" &&
    PUZZLE_BUBBLE_KINDS.includes(value as PuzzleBubbleKind)
  );
}

function isPuzzleBotDifficulty(value: unknown): value is PuzzleBotDifficulty {
  return (
    typeof value === "string" &&
    PUZZLE_BOT_DIFFICULTIES.includes(value as PuzzleBotDifficulty)
  );
}

function isPuzzleColor(value: unknown): value is PuzzleColor {
  return (
    typeof value === "string" &&
    PUZZLE_COLORS.includes(value as PuzzleColor)
  );
}

function isPuzzleLaunchBubble(value: unknown): value is PuzzleLaunchBubble {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const bubble = value as Record<string, unknown>;
  return isPuzzleColor(bubble.color) && isPuzzleBubbleKind(bubble.kind);
}

/**
 * The opening normal shot is followed by an immediately testable color burst.
 * Starting with shot seven, every fifth shot cycles row, column, then color.
 */
export function puzzleBubbleKindForShot(ordinal: number): PuzzleBubbleKind {
  if (ordinal === 2) {
    return "color_burst";
  }
  if (ordinal >= 7 && (ordinal - 7) % 5 === 0) {
    const cycle = ["row_burst", "column_burst", "color_burst"] as const;
    return cycle[Math.floor((ordinal - 7) / 5) % cycle.length] ?? "normal";
  }
  return "normal";
}

function otherTeam(team: PuzzleTeamId): PuzzleTeamId {
  return team === "blue" ? "orange" : "blue";
}

function cloneInput(input: PuzzleInput): PuzzleInput {
  return { aim: input.aim, fire: input.fire, hold: input.hold === true };
}

function clampAim(aim: number): number {
  if (!Number.isFinite(aim)) {
    return 0;
  }
  return Math.max(PUZZLE_MIN_AIM, Math.min(PUZZLE_MAX_AIM, aim));
}

function normalizeInput(
  input: PuzzleInput | undefined,
  previousAim: number,
): PuzzleInput {
  if (input === undefined) {
    return { aim: previousAim, fire: false };
  }
  return {
    aim: Number.isFinite(input.aim) ? clampAim(input.aim) : previousAim,
    fire: input.fire === true,
    hold: input.hold === true,
  };
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function mixSeed(seed: number, value: string): number {
  let mixed = (seed ^ stableHash(value)) >>> 0;
  mixed ^= mixed >>> 16;
  mixed = Math.imul(mixed, 2_246_822_519) >>> 0;
  mixed ^= mixed >>> 13;
  mixed = Math.imul(mixed, 3_266_489_917) >>> 0;
  mixed ^= mixed >>> 16;
  return mixed === 0 ? 0x6d2b79f5 : mixed >>> 0;
}

function advanceRandom(state: number): RandomStep {
  const next = (Math.imul(state, 1_664_525) + 1_013_904_223) >>> 0;
  return { state: next, unit: next / 4_294_967_296 };
}

function drawRandomIndex(board: PuzzleBoardState, length: number): number {
  const next = advanceRandom(board.rngState);
  board.rngState = next.state;
  return Math.min(length - 1, Math.floor(next.unit * length));
}

function colorsOnBoard(board: PuzzleBoardState): PuzzleColor[] {
  const present = new Set(board.cells.map((cell) => cell.color));
  const colors = PUZZLE_COLORS.filter((color) => present.has(color));
  return colors.length > 0 ? colors : [...PUZZLE_COLORS];
}

function drawLaunchColor(
  board: PuzzleBoardState,
  avoidColor?: PuzzleColor,
): PuzzleColor {
  const colors = colorsOnBoard(board);
  // Favor the rarest live color so a board cannot drift into a long dominant-
  // color streak. Ties remain seeded and deterministic for multiplayer replay.
  const counts = new Map<PuzzleColor, number>(
    colors.map((color) => [
      color,
      board.cells.reduce(
        (total, cell) => total + (cell.color === color ? 1 : 0),
        0,
      ),
    ]),
  );
  const minimumCount = Math.min(...counts.values());
  const leastCommon = colors.filter((color) => counts.get(color) === minimumCount);
  const candidates = avoidColor !== undefined && leastCommon.length > 1
    ? leastCommon.filter((color) => color !== avoidColor)
    : leastCommon;
  return candidates[drawRandomIndex(board, candidates.length)] ?? PUZZLE_COLORS[0]!;
}

function shuffledInitialColors(board: PuzzleBoardState): PuzzleColor[] {
  const colors = [...PUZZLE_COLORS];
  for (let index = colors.length - 1; index > 0; index -= 1) {
    const swapIndex = drawRandomIndex(board, index + 1);
    const current = colors[index];
    colors[index] = colors[swapIndex] ?? colors[0]!;
    colors[swapIndex] = current ?? colors[0]!;
  }
  return colors.slice(0, 4);
}

function makeInitialBoard(
  player: PuzzlePlayerSlot,
  seed: number,
  round: number,
): PuzzleBoardState {
  const board: PuzzleBoardState = {
    playerId: player.id,
    team: player.team,
    cells: [],
    activeShot: null,
    aim: 0,
    currentColor: PUZZLE_COLORS[0]!,
    nextColor: PUZZLE_COLORS[1]!,
    currentKind: "normal",
    nextKind: "color_burst",
    heldBubble: null,
    holdLocked: false,
    nextBubbleOrdinal: 2,
    lastShotResolvedTick: -1,
    dangerLineY: PUZZLE_DANGER_LINE_Y,
    pendingPressureRows: 0,
    pendingAttackBubbles: 0,
    combo: 0,
    bestCombo: 0,
    misses: 0,
    status: "active",
    score: 0,
    shotsResolved: 0,
    shotsFired: 0,
    rngState: mixSeed(seed, `round:${round}:stage`),
    lastInput: cloneInput(PUZZLE_EMPTY_INPUT),
  };

  const activeColors = shuffledInitialColors(board);
  for (let row = 0; row < PUZZLE_INITIAL_ROWS; row += 1) {
    for (let col = 0; col < PUZZLE_BOARD_COLUMNS; col += 1) {
      const pair = Math.floor(col / 2);
      const color =
        activeColors[(pair + row) % activeColors.length] ?? activeColors[0]!;
      board.cells.push({ row, col, color });
    }
  }

  board.currentColor = drawLaunchColor(board);
  board.nextColor = drawLaunchColor(board, board.currentColor);
  return board;
}

function validateSlots(
  mode: CreatePuzzleMatchOptions["mode"],
  slots: PuzzlePlayerSlot[],
): void {
  const expectedSlots = mode === "duel" ? 2 : 4;
  if (slots.length !== expectedSlots) {
    throw new Error(`${mode} puzzle matches require exactly ${expectedSlots} slots`);
  }

  const ids = new Set<string>();
  const teamCounts = { blue: 0, orange: 0 };
  for (const slot of slots) {
    if (slot.id.trim().length === 0 || slot.name.trim().length === 0) {
      throw new Error("Puzzle player ids and names must not be empty");
    }
    if (ids.has(slot.id)) {
      throw new Error(`Duplicate puzzle player id: ${slot.id}`);
    }
    if (slot.team !== "blue" && slot.team !== "orange") {
      throw new Error(`Invalid puzzle team for ${slot.id}`);
    }
    if (slot.kind !== "human" && slot.kind !== "bot") {
      throw new Error(`Invalid puzzle player kind for ${slot.id}`);
    }
    if (
      slot.botDifficulty !== undefined &&
      !isPuzzleBotDifficulty(slot.botDifficulty)
    ) {
      throw new Error(`Invalid puzzle bot difficulty for ${slot.id}`);
    }
    ids.add(slot.id);
    teamCounts[slot.team] += 1;
  }

  const expectedPerTeam = expectedSlots / 2;
  if (
    teamCounts.blue !== expectedPerTeam ||
    teamCounts.orange !== expectedPerTeam
  ) {
    throw new Error(`${mode} puzzle matches require balanced teams`);
  }
}

function makeBoards(
  players: PuzzlePlayerSlot[],
  seed: number,
  round: number,
): Record<string, PuzzleBoardState> {
  const boards: Record<string, PuzzleBoardState> = {};
  for (const player of players) {
    boards[player.id] = makeInitialBoard(player, seed, round);
  }
  return boards;
}

export function createPuzzleMatchState(
  options: CreatePuzzleMatchOptions,
): PuzzleMatchState {
  if (options.id.trim().length === 0) {
    throw new Error("Puzzle match id must not be empty");
  }
  validateSlots(options.mode, options.slots);

  const seed = (options.seed ?? stableHash(options.id)) >>> 0;
  const players = options.slots.map((slot) => ({
    id: slot.id,
    name: slot.name,
    team: slot.team,
    kind: slot.kind,
    ...(slot.kind === "bot"
      ? { botDifficulty: slot.botDifficulty ?? "medium" }
      : {}),
    connected: slot.connected ?? true,
  }));

  return {
    schemaVersion: 3,
    id: options.id,
    mode: options.mode,
    seed,
    tick: 0,
    phase: "countdown",
    phaseTicksRemaining: PUZZLE_COUNTDOWN_TICKS,
    round: 1,
    roundTicksRemaining: PUZZLE_ROUND_TICKS,
    overtime: false,
    overtimeTicks: 0,
    players,
    boards: makeBoards(players, seed, 1),
    scores: { blue: 0, orange: 0 },
    roundWins: { blue: 0, orange: 0 },
    roundWinner: null,
    matchWinner: null,
    sideByTeam: { blue: "left", orange: "right" },
    pressureCursor: { blue: 0, orange: 0 },
    nextShotId: 1,
  };
}

function nonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : fallback;
}

function integerAtLeast(value: unknown, minimum: number, fallback: number): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= minimum
    ? value
    : fallback;
}

function upgradePuzzleMatchStateInPlace(state: PuzzleMatchState): void {
  const rawState = state as unknown as {
    schemaVersion: number;
    players: Array<Record<string, unknown>>;
    boards: Record<string, Record<string, unknown>>;
  };
  for (const player of rawState.players) {
    if (player.kind === "bot") {
      player.botDifficulty = isPuzzleBotDifficulty(player.botDifficulty)
        ? player.botDifficulty
        : "medium";
    }
  }
  for (const rawBoard of Object.values(rawState.boards)) {
    const board = rawBoard as unknown as PuzzleBoardState;
    const activeShot = rawBoard.activeShot;
    const inferredShotsFired =
      nonNegativeInteger(rawBoard.shotsResolved, 0) +
      (activeShot !== null && typeof activeShot === "object" ? 1 : 0);
    board.shotsFired = nonNegativeInteger(rawBoard.shotsFired, inferredShotsFired);
    board.combo = nonNegativeInteger(rawBoard.combo, 0);
    board.bestCombo = Math.max(
      board.combo,
      nonNegativeInteger(rawBoard.bestCombo, board.combo),
    );
    board.pendingPressureRows = Math.min(
      Math.floor(
        PUZZLE_MAX_PENDING_ATTACK_BUBBLES / PUZZLE_ATTACK_BUBBLES_PER_ROW,
      ),
      nonNegativeInteger(rawBoard.pendingPressureRows, 0),
    );
    const remainingAttackCapacity = Math.max(
      0,
      PUZZLE_MAX_PENDING_ATTACK_BUBBLES -
        board.pendingPressureRows * PUZZLE_ATTACK_BUBBLES_PER_ROW,
    );
    board.pendingAttackBubbles = Math.min(
      remainingAttackCapacity,
      nonNegativeInteger(rawBoard.pendingAttackBubbles, 0),
    );
    board.currentKind = isPuzzleBubbleKind(rawBoard.currentKind)
      ? rawBoard.currentKind
      : "normal";
    board.nextKind = isPuzzleBubbleKind(rawBoard.nextKind)
      ? rawBoard.nextKind
      : "normal";
    board.heldBubble = isPuzzleLaunchBubble(rawBoard.heldBubble)
      ? {
          color: rawBoard.heldBubble.color,
          kind: rawBoard.heldBubble.kind,
        }
      : null;
    board.holdLocked = rawBoard.holdLocked === true;
    board.nextBubbleOrdinal = integerAtLeast(
      rawBoard.nextBubbleOrdinal,
      2,
      board.shotsFired + 2,
    );
    board.lastShotResolvedTick = integerAtLeast(
      rawBoard.lastShotResolvedTick,
      -1,
      -1,
    );
    // Clearing the ceiling is a reward event, not a terminal board state.
    // Revive the only historical non-loss status if an old live snapshot is
    // restored after this rule change.
    if (state.phase === "playing" && board.status === "cleared") {
      board.status = "active";
    }
    // Geometry is a live balance contract rather than persisted player data.
    // Rebase legacy schema-v3 snapshots so their danger threshold cannot retain
    // the shorter pre-v0.4 board value.
    board.dangerLineY = PUZZLE_DANGER_LINE_Y;
    const rawLastInput = rawBoard.lastInput;
    if (
      typeof rawLastInput === "object" &&
      rawLastInput !== null &&
      !Array.isArray(rawLastInput)
    ) {
      (rawLastInput as Record<string, unknown>).hold =
        (rawLastInput as Record<string, unknown>).hold === true;
    } else {
      board.lastInput = { ...PUZZLE_EMPTY_INPUT };
    }

    if (activeShot !== null && typeof activeShot === "object") {
      const rawShot = activeShot as Record<string, unknown>;
      (activeShot as PuzzleBoardState["activeShot"] & Record<string, unknown>)![
        "kind"
      ] = isPuzzleBubbleKind(rawShot.kind) ? rawShot.kind : "normal";
    }
  }
  rawState.schemaVersion = 3;
}

/**
 * Pure v1/v2-to-v3 restore helper. The persisted/historical input object is never
 * edited; callers receive an independently serializable upgraded snapshot.
 */
export function upgradePuzzleMatchState(
  state: PuzzleMatchState,
): PuzzleMatchState {
  const upgraded = JSON.parse(JSON.stringify(state)) as PuzzleMatchState;
  upgradePuzzleMatchStateInPlace(upgraded);
  return upgraded;
}

/** Compatibility alias retained for adapters already importing this name. */
export const hydratePuzzleMatchState = upgradePuzzleMatchState;

export function resetPuzzleMatchForDemo(
  state: PuzzleMatchState,
): PuzzleMatchState {
  const fresh = createPuzzleMatchState({
    id: state.id,
    mode: state.mode,
    slots: state.players.map((player) => ({
      id: player.id,
      name: player.name,
      team: player.team,
      kind: player.kind,
      ...(player.botDifficulty === undefined
        ? {}
        : { botDifficulty: player.botDifficulty }),
      connected: player.connected ?? true,
    })),
    seed: state.seed,
  });
  Object.assign(state, fresh);
  return state;
}

function startNextRound(state: PuzzleMatchState): void {
  state.round += 1;
  state.phase = "countdown";
  state.phaseTicksRemaining = PUZZLE_COUNTDOWN_TICKS;
  state.roundTicksRemaining = PUZZLE_ROUND_TICKS;
  state.overtime = false;
  state.overtimeTicks = 0;
  state.boards = makeBoards(state.players, state.seed, state.round);
  state.scores = { blue: 0, orange: 0 };
  state.roundWinner = null;
  state.pressureCursor = { blue: 0, orange: 0 };
  state.sideByTeam =
    state.round % 2 === 1
      ? { blue: "left", orange: "right" }
      : { blue: "right", orange: "left" };
}

function finishRound(
  state: PuzzleMatchState,
  winner: PuzzleTeamId,
  events: PuzzleEvent[],
): void {
  state.roundWinner = winner;
  state.roundWins[winner] += 1;
  for (const board of Object.values(state.boards)) {
    board.activeShot = null;
  }

  events.push({
    type: "round_ended",
    tick: state.tick,
    round: state.round,
    winner,
    roundWins: { ...state.roundWins },
  });

  if (state.roundWins[winner] >= PUZZLE_ROUNDS_TO_WIN) {
    state.phase = "match_end";
    state.phaseTicksRemaining = 0;
    state.matchWinner = winner;
    events.push({
      type: "match_ended",
      tick: state.tick,
      winner,
      roundWins: { ...state.roundWins },
    });
    return;
  }

  state.phase = "round_end";
  state.phaseTicksRemaining = PUZZLE_ROUND_END_TICKS;
}

function beginOvertime(state: PuzzleMatchState, events: PuzzleEvent[]): void {
  if (state.overtime) {
    return;
  }
  state.overtime = true;
  state.overtimeTicks = 0;
  state.roundTicksRemaining = 0;
  events.push({ type: "overtime_started", tick: state.tick });
}

function launchBubble(
  color: PuzzleColor,
  kind: PuzzleBubbleKind,
): PuzzleLaunchBubble {
  return { color, kind };
}

function advanceLaunchQueue(board: PuzzleBoardState): void {
  board.currentColor = board.nextColor;
  board.currentKind = board.nextKind;
  board.nextBubbleOrdinal += 1;
  // NOW and NEXT are promises to the player. Only the newly generated NEXT may
  // consult the live board palette; displayed bubbles are never rewritten.
  board.nextColor = drawLaunchColor(board, board.currentColor);
  board.nextKind = puzzleBubbleKindForShot(board.nextBubbleOrdinal);
}

function holdLauncherBubble(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  events: PuzzleEvent[],
): boolean {
  if (
    board.status !== "active" ||
    board.activeShot !== null ||
    board.holdLocked
  ) {
    return false;
  }

  const outgoing = launchBubble(board.currentColor, board.currentKind);
  if (board.heldBubble === null) {
    board.heldBubble = outgoing;
    advanceLaunchQueue(board);
  } else {
    const incoming = board.heldBubble;
    board.heldBubble = outgoing;
    board.currentColor = incoming.color;
    board.currentKind = incoming.kind;
  }
  board.holdLocked = true;
  events.push({
    type: "bubble_held",
    tick: state.tick,
    playerId: board.playerId,
    heldBubble: { ...board.heldBubble },
    currentBubble: launchBubble(board.currentColor, board.currentKind),
    nextBubble: launchBubble(board.nextColor, board.nextKind),
  });
  return true;
}

function fireShot(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  events: PuzzleEvent[],
): void {
  const angleRadians = (board.aim * Math.PI) / 180;
  const shotId = state.nextShotId;
  state.nextShotId += 1;
  const color = board.currentColor;
  const kind = board.currentKind;
  board.activeShot = {
    id: shotId,
    ownerId: board.playerId,
    color,
    kind,
    x: PUZZLE_SHOOTER_X,
    y: PUZZLE_SHOOTER_Y,
    vx: Math.sin(angleRadians) * PUZZLE_PROJECTILE_SPEED,
    vy: -Math.cos(angleRadians) * PUZZLE_PROJECTILE_SPEED,
    ageTicks: 0,
    wallBounces: 0,
  };
  board.shotsFired += 1;
  advanceLaunchQueue(board);
  events.push({
    type: "fired",
    tick: state.tick,
    playerId: board.playerId,
    shotId,
    color,
    kind,
    aim: board.aim,
  });
}

function touchedCell(
  x: number,
  y: number,
  cells: ReadonlyArray<PuzzleCell>,
): PuzzleCell | null {
  const collisionDistance = PUZZLE_BUBBLE_RADIUS * 2 - 0.01;
  const collisionDistanceSquared = collisionDistance * collisionDistance;
  let result: PuzzleCell | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const cell of cells) {
    const point = puzzleCellToPoint(cell);
    const dx = x - point.x;
    const dy = y - point.y;
    const distance = dx * dx + dy * dy;
    if (
      distance <= collisionDistanceSquared &&
      (distance < bestDistance ||
        (distance === bestDistance &&
          (result === null ||
            cell.row < result.row ||
            (cell.row === result.row && cell.col < result.col))))
    ) {
      result = cell;
      bestDistance = distance;
    }
  }
  return result;
}

function updateActiveShot(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  events: PuzzleEvent[],
): ShotImpact | null {
  const shot = board.activeShot;
  if (shot === null) {
    return null;
  }

  shot.ageTicks += 1;
  let bouncedThisTick = false;
  for (let substep = 0; substep < PUZZLE_SHOT_SUBSTEPS; substep += 1) {
    shot.x +=
      (shot.vx * PUZZLE_FIXED_DELTA_SECONDS) / PUZZLE_SHOT_SUBSTEPS;
    shot.y +=
      (shot.vy * PUZZLE_FIXED_DELTA_SECONDS) / PUZZLE_SHOT_SUBSTEPS;

    if (shot.x < PUZZLE_BUBBLE_RADIUS) {
      shot.x = PUZZLE_BUBBLE_RADIUS + (PUZZLE_BUBBLE_RADIUS - shot.x);
      shot.vx = Math.abs(shot.vx);
      shot.wallBounces += 1;
      bouncedThisTick = true;
    } else if (shot.x > PUZZLE_BOARD_WIDTH - PUZZLE_BUBBLE_RADIUS) {
      const right = PUZZLE_BOARD_WIDTH - PUZZLE_BUBBLE_RADIUS;
      shot.x = right - (shot.x - right);
      shot.vx = -Math.abs(shot.vx);
      shot.wallBounces += 1;
      bouncedThisTick = true;
    }

    const ceiling = shot.y <= PUZZLE_BUBBLE_RADIUS;
    const collisionCell = ceiling ? null : touchedCell(shot.x, shot.y, board.cells);
    if (ceiling || collisionCell !== null) {
      if (bouncedThisTick) {
        events.push({
          type: "wall_bounced",
          tick: state.tick,
          playerId: board.playerId,
          shotId: shot.id,
          x: shot.x,
        });
      }
      return {
        playerId: board.playerId,
        shotId: shot.id,
        x: shot.x,
        y: Math.max(PUZZLE_BUBBLE_RADIUS, shot.y),
        collisionCell:
          collisionCell === null
            ? null
            : { row: collisionCell.row, col: collisionCell.col },
        ceiling,
      };
    }
  }

  if (bouncedThisTick) {
    events.push({
      type: "wall_bounced",
      tick: state.tick,
      playerId: board.playerId,
      shotId: shot.id,
      x: shot.x,
    });
  }

  if (
    shot.ageTicks >= PUZZLE_MAX_SHOT_TICKS ||
    shot.y < -PUZZLE_BUBBLE_RADIUS ||
    shot.y > PUZZLE_BOARD_HEIGHT + PUZZLE_BUBBLE_RADIUS
  ) {
    return {
      playerId: board.playerId,
      shotId: shot.id,
      x: shot.x,
      y: Math.max(PUZZLE_BUBBLE_RADIUS, Math.min(PUZZLE_BOARD_HEIGHT, shot.y)),
      collisionCell: null,
      ceiling: shot.y <= PUZZLE_BUBBLE_RADIUS,
    };
  }

  return null;
}

function nearestLegalSnapCell(
  cells: ReadonlyArray<PuzzleCell>,
  x: number,
  y: number,
  collisionCell: PuzzleGridPosition | null,
  ceiling: boolean,
): PuzzleGridPosition | null {
  const legal = getPuzzleLegalSnapCells(cells);
  const preferredKeys = new Set<string>();
  if (collisionCell !== null) {
    for (const neighbor of getPuzzleNeighborPositions(
      collisionCell.row,
      collisionCell.col,
    )) {
      preferredKeys.add(puzzleCellKey(neighbor));
    }
  } else if (ceiling) {
    for (let col = 0; col < PUZZLE_BOARD_COLUMNS; col += 1) {
      preferredKeys.add(puzzleCellKey({ row: 0, col }));
    }
  }
  const preferred = legal.filter((candidate) =>
    preferredKeys.has(puzzleCellKey(candidate)),
  );
  const candidates = preferred.length > 0 ? preferred : legal;
  let best: PuzzleGridPosition | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    const point = puzzleCellToPoint(candidate);
    const dx = x - point.x;
    const dy = y - point.y;
    const distance = dx * dx + dy * dy;
    if (
      distance < bestDistance - 0.000_001 ||
      (Math.abs(distance - bestDistance) <= 0.000_001 &&
        (best === null ||
          candidate.row < best.row ||
          (candidate.row === best.row && candidate.col < best.col)))
    ) {
      best = candidate;
      bestDistance = distance;
    }
  }

  return best;
}

function connectedCells(
  cells: ReadonlyArray<PuzzleCell>,
  starts: ReadonlyArray<PuzzleCell>,
  color: PuzzleColor | null,
): PuzzleCell[] {
  const byKey = new Map(cells.map((cell) => [puzzleCellKey(cell), cell]));
  const visited = new Set<string>();
  const queue = [...starts];
  const result: PuzzleCell[] = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) {
      continue;
    }
    const key = puzzleCellKey(current);
    if (visited.has(key)) {
      continue;
    }
    const actual = byKey.get(key);
    if (actual === undefined || (color !== null && actual.color !== color)) {
      continue;
    }
    visited.add(key);
    result.push(actual);

    for (const neighbor of getPuzzleNeighborPositions(actual.row, actual.col)) {
      const candidate = byKey.get(puzzleCellKey(neighbor));
      if (candidate !== undefined && !visited.has(puzzleCellKey(candidate))) {
        queue.push(candidate);
      }
    }
  }

  sortPuzzleCells(result);
  return result;
}

function removeCells(
  cells: PuzzleCell[],
  removed: ReadonlyArray<PuzzleCell>,
): PuzzleCell[] {
  const removedKeys = new Set(removed.map(puzzleCellKey));
  return cells.filter((cell) => !removedKeys.has(puzzleCellKey(cell)));
}

function unsupportedCells(cells: ReadonlyArray<PuzzleCell>): PuzzleCell[] {
  const ceiling = cells.filter((cell) => cell.row === 0);
  const supported = new Set(
    connectedCells(cells, ceiling, null).map(puzzleCellKey),
  );
  const dropped = cells.filter((cell) => !supported.has(puzzleCellKey(cell)));
  sortPuzzleCells(dropped);
  return dropped;
}

function comboRows(combo: number): number {
  if (combo < 3) {
    return 0;
  }
  return combo < 6 ? 1 : PUZZLE_MAX_ATTACK_ROWS;
}

function queueAttack(
  state: PuzzleMatchState,
  sourceBoard: PuzzleBoardState,
  rows: number,
  bubbles: number,
  events: PuzzleEvent[],
): void {
  if (rows <= 0 && bubbles <= 0) {
    return;
  }
  const opponents = state.players
    .filter((player) => {
      const board = state.boards[player.id];
      return player.team !== sourceBoard.team && board?.status === "active";
    })
    .sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    );
  if (opponents.length === 0) {
    return;
  }

  const cursor = state.pressureCursor[sourceBoard.team];
  const target = opponents[cursor % opponents.length];
  if (target === undefined) {
    return;
  }
  state.pressureCursor[sourceBoard.team] = cursor + 1;
  const targetBoard = state.boards[target.id];
  if (targetBoard === undefined) {
    return;
  }

  const queuedPhysicalBubbles =
    targetBoard.pendingPressureRows * PUZZLE_ATTACK_BUBBLES_PER_ROW +
    targetBoard.pendingAttackBubbles;
  let remainingCapacity = Math.max(
    0,
    PUZZLE_MAX_PENDING_ATTACK_BUBBLES - queuedPhysicalBubbles,
  );
  const addedRows = Math.min(
    rows,
    Math.floor(remainingCapacity / PUZZLE_ATTACK_BUBBLES_PER_ROW),
  );
  remainingCapacity -= addedRows * PUZZLE_ATTACK_BUBBLES_PER_ROW;
  const addedBubbles = Math.min(bubbles, remainingCapacity);
  targetBoard.pendingPressureRows += addedRows;
  targetBoard.pendingAttackBubbles += addedBubbles;

  if (addedRows === 0 && addedBubbles === 0) {
    return;
  }
  events.push({
    type: "pressure_queued",
    tick: state.tick,
    fromPlayerId: sourceBoard.playerId,
    targetPlayerId: target.id,
    rows: addedRows,
    rawTransferBubbles: bubbles,
    bubbles: addedBubbles,
    totalBubbles:
      addedRows * PUZZLE_ATTACK_BUBBLES_PER_ROW + addedBubbles,
    combo: sourceBoard.combo,
  });
}

function cellsForSpecial(
  board: PuzzleBoardState,
  placed: PuzzleCell,
  kind: Exclude<PuzzleBubbleKind, "normal">,
): PuzzleCell[] {
  let selected: PuzzleCell[];
  if (kind === "color_burst") {
    selected = board.cells.filter((cell) => cell.color === placed.color);
  } else if (kind === "row_burst") {
    selected = board.cells.filter((cell) => cell.row === placed.row);
  } else {
    const impactX = puzzleCellToPoint(placed).x;
    selected = [];
    for (let row = 0; row < PUZZLE_BOARD_ROWS; row += 1) {
      const nearest = board.cells
        .filter((cell) => cell.row === row)
        .map((cell) => ({
          cell,
          distance: Math.abs(puzzleCellToPoint(cell).x - impactX),
        }))
        .filter((candidate) => candidate.distance <= PUZZLE_BUBBLE_RADIUS + 0.001)
        .sort(
          (left, right) =>
            left.distance - right.distance || left.cell.col - right.cell.col,
        )[0];
      if (nearest !== undefined) {
        selected.push(nearest.cell);
      }
    }
  }
  sortPuzzleCells(selected);
  return selected;
}

function resolveShot(
  state: PuzzleMatchState,
  impact: ShotImpact,
  events: PuzzleEvent[],
): ResolutionResult {
  const board = state.boards[impact.playerId];
  const shot = board?.activeShot;
  if (
    board === undefined ||
    shot === undefined ||
    shot === null ||
    shot.id !== impact.shotId
  ) {
    return {
      playerId: impact.playerId,
      blocked: false,
      cleared: false,
      missPressureRows: 0,
    };
  }

  board.activeShot = null;
  board.shotsResolved += 1;
  board.holdLocked = false;
  board.lastShotResolvedTick = state.tick;
  const position = nearestLegalSnapCell(
    board.cells,
    impact.x,
    impact.y,
    impact.collisionCell,
    impact.ceiling,
  );
  if (position === null) {
    return {
      playerId: impact.playerId,
      blocked: true,
      cleared: false,
      missPressureRows: 0,
    };
  }

  const placed: PuzzleCell = {
    row: position.row,
    col: position.col,
    color: shot.color,
  };
  board.cells.push(placed);
  sortPuzzleCells(board.cells);
  events.push({
    type: "shot_snapped",
    tick: state.tick,
    playerId: board.playerId,
    shotId: shot.id,
    cell: { ...placed },
    kind: shot.kind,
  });

  let directRemoved: PuzzleCell[] = [];
  if (shot.kind === "normal") {
    const matched = connectedCells(board.cells, [placed], placed.color);
    directRemoved = matched.length >= 3 ? matched : [];
  } else {
    directRemoved = cellsForSpecial(board, placed, shot.kind);
  }

  if (directRemoved.length > 0) {
    board.cells = removeCells(board.cells, directRemoved);
    const score = directRemoved.length * PUZZLE_MATCH_POINTS_PER_BUBBLE;
    board.score += score;
    state.scores[board.team] += score;
    if (shot.kind === "normal") {
      events.push({
        type: "matched",
        tick: state.tick,
        playerId: board.playerId,
        cells: directRemoved.map((cell) => ({ ...cell })),
        score,
      });
    } else {
      events.push({
        type: "special_triggered",
        tick: state.tick,
        playerId: board.playerId,
        shotId: shot.id,
        kind: shot.kind,
        color: shot.color,
        origin: { ...placed },
        cells: directRemoved.map((cell) => ({ ...cell })),
        score,
      });
    }
  }

  const dropped =
    directRemoved.length > 0 ? unsupportedCells(board.cells) : ([] as PuzzleCell[]);
  if (dropped.length > 0) {
    board.cells = removeCells(board.cells, dropped);
    const score = dropped.length * PUZZLE_DROP_POINTS_PER_BUBBLE;
    board.score += score;
    state.scores[board.team] += score;
    events.push({
      type: "dropped",
      tick: state.tick,
      playerId: board.playerId,
      cells: dropped.map((cell) => ({ ...cell })),
      score,
    });
  }

  const previousCombo = board.combo;
  if (directRemoved.length > 0) {
    board.combo += 1;
    board.bestCombo = Math.max(board.bestCombo, board.combo);
  } else {
    board.combo = 0;
  }
  const bonusRows = comboRows(board.combo);
  if (directRemoved.length > 0) {
    events.push({
      type: "combo_changed",
      tick: state.tick,
      playerId: board.playerId,
      previousCombo,
      combo: board.combo,
      bestCombo: board.bestCombo,
      bonusRows,
      directRemoved: directRemoved.length,
      dropped: dropped.length,
    });
  } else if (previousCombo > 0) {
    events.push({
      type: "combo_broken",
      tick: state.tick,
      playerId: board.playerId,
      previousCombo,
      combo: 0,
      bestCombo: board.bestCombo,
      bonusRows: 0,
      directRemoved: 0,
      dropped: 0,
    });
  }

  const transferSourceBubbles =
    Math.max(0, directRemoved.length - 3) + dropped.length;
  const transferBubbles = Math.floor(transferSourceBubbles / 3);
  queueAttack(state, board, bonusRows, transferBubbles, events);

  let missPressureRows = 0;
  if (directRemoved.length === 0) {
    board.misses += 1;
    if (board.misses >= PUZZLE_MISSES_PER_PRESSURE) {
      board.misses = 0;
      missPressureRows = 1;
    }
  } else {
    board.misses = 0;
  }

  return {
    playerId: board.playerId,
    blocked: false,
    cleared: board.cells.length === 0,
    missPressureRows,
  };
}

function applyPressureRows(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  rows: number,
  source: PuzzlePressureSource,
  events: PuzzleEvent[],
): void {
  if (rows <= 0 || board.status !== "active") {
    return;
  }

  board.cells = board.cells.map((cell) => ({
    row: cell.row + rows,
    col: cell.col,
    color: cell.color,
  }));

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < PUZZLE_BOARD_COLUMNS; col += 1) {
      const color =
        PUZZLE_COLORS[drawRandomIndex(board, PUZZLE_COLORS.length)] ??
        PUZZLE_COLORS[0]!;
      board.cells.push({ row, col, color });
    }
  }
  sortPuzzleCells(board.cells);
  events.push({
    type: "pressure_applied",
    tick: state.tick,
    playerId: board.playerId,
    rows,
    source,
  });
}

function chooseBottomAttackCell(
  board: PuzzleBoardState,
  baseBottomRow: number,
  attackIndex: number,
): PuzzleGridPosition | null {
  const maximumBandRow = Math.min(
    PUZZLE_BOARD_ROWS - 1,
    baseBottomRow + 1 + Math.floor(attackIndex / PUZZLE_BOARD_COLUMNS),
  );
  const legal = getPuzzleLegalSnapCells(board.cells).filter(
    (candidate) => candidate.row <= maximumBandRow,
  );
  legal.sort((left, right) => {
    if (left.row !== right.row) {
      return right.row - left.row;
    }
    const leftX = puzzleCellToPoint(left).x;
    const rightX = puzzleCellToPoint(right).x;
    const leftCenterDistance = Math.abs(leftX - PUZZLE_SHOOTER_X);
    const rightCenterDistance = Math.abs(rightX - PUZZLE_SHOOTER_X);
    return leftCenterDistance - rightCenterDistance || left.col - right.col;
  });
  return legal[0] ?? null;
}

/** Attack payloads hang below the target cluster; they never push the ceiling. */
function applyBottomAttack(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  rows: number,
  bubbles: number,
  events: PuzzleEvent[],
): void {
  if ((rows <= 0 && bubbles <= 0) || board.status !== "active") {
    return;
  }

  const requestedTotal =
    rows * PUZZLE_ATTACK_BUBBLES_PER_ROW + bubbles;
  const baseBottomRow = board.cells.reduce(
    (maximum, cell) => Math.max(maximum, cell.row),
    -1,
  );
  const palette = colorsOnBoard(board);
  const attached: PuzzleCell[] = [];
  for (let index = 0; index < requestedTotal; index += 1) {
    const position = chooseBottomAttackCell(board, baseBottomRow, index);
    if (position === null) {
      break;
    }
    const color =
      palette[drawRandomIndex(board, palette.length)] ?? PUZZLE_COLORS[0]!;
    const cell = { row: position.row, col: position.col, color };
    board.cells.push(cell);
    attached.push(cell);
  }
  sortPuzzleCells(board.cells);
  sortPuzzleCells(attached);
  events.push({
    type: "pressure_applied",
    tick: state.tick,
    playerId: board.playerId,
    rows,
    source: "attack",
    bubbles,
    totalBubbles: requestedTotal,
    cells: attached.map((cell) => ({ ...cell })),
  });
}

function boardCrossesDanger(board: PuzzleBoardState): boolean {
  return board.cells.some((cell) => {
    if (cell.row >= PUZZLE_BOARD_ROWS) {
      return true;
    }
    return puzzleCellToPoint(cell).y + PUZZLE_BUBBLE_RADIUS >= board.dangerLineY;
  });
}

function markBoardLost(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  reason: "danger" | "blocked",
  events: PuzzleEvent[],
): void {
  if (board.status !== "active") {
    return;
  }
  board.status = "lost";
  board.activeShot = null;
  events.push({
    type: "board_lost",
    tick: state.tick,
    playerId: board.playerId,
    team: board.team,
    reason,
  });
}

function reportBoardCleared(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  events: PuzzleEvent[],
): void {
  if (board.status !== "active") {
    return;
  }
  events.push({
    type: "board_cleared",
    tick: state.tick,
    playerId: board.playerId,
    team: board.team,
  });
}

function teamCellCounts(state: PuzzleMatchState): { blue: number; orange: number } {
  const result = { blue: 0, orange: 0 };
  for (const player of state.players) {
    result[player.team] += state.boards[player.id]?.cells.length ?? 0;
  }
  return result;
}

function fewerCellsWinner(state: PuzzleMatchState): PuzzleTeamId | null {
  const counts = teamCellCounts(state);
  if (counts.blue === counts.orange) {
    return null;
  }
  return counts.blue < counts.orange ? "blue" : "orange";
}

/**
 * Simultaneous equal clears/losses are terminal and must not strand a round in
 * playing forever. Score is the meaningful first tie-break; a seeded round
 * coin is the final stable fallback when every observable measure is equal.
 */
function terminalTieWinner(state: PuzzleMatchState): PuzzleTeamId {
  if (state.scores.blue !== state.scores.orange) {
    return state.scores.blue > state.scores.orange ? "blue" : "orange";
  }
  return (mixSeed(state.seed, `terminal-tie:${state.round}`) & 1) === 0
    ? "blue"
    : "orange";
}

function statusWinner(state: PuzzleMatchState): PuzzleTeamId | null {
  const lost = new Set<PuzzleTeamId>();
  for (const board of Object.values(state.boards)) {
    if (board.status === "lost") {
      lost.add(board.team);
    }
  }

  if (lost.size === 1) {
    const loser = [...lost][0];
    return loser === undefined ? null : otherTeam(loser);
  }
  if (lost.size > 1) {
    return fewerCellsWinner(state) ?? terminalTieWinner(state);
  }
  return null;
}

function updateOvertime(
  state: PuzzleMatchState,
  events: PuzzleEvent[],
): PuzzleTeamId | null {
  state.overtimeTicks += 1;
  if (
    state.overtimeTicks % PUZZLE_OVERTIME_PRESSURE_INTERVAL_TICKS ===
    0
  ) {
    for (const player of state.players) {
      const board = state.boards[player.id];
      if (board !== undefined) {
        applyPressureRows(state, board, 1, "overtime", events);
      }
    }
  }

  for (const board of Object.values(state.boards)) {
    if (board.status === "active" && boardCrossesDanger(board)) {
      markBoardLost(state, board, "danger", events);
    }
  }

  const winnerFromStatus = statusWinner(state);
  if (winnerFromStatus !== null) {
    return winnerFromStatus;
  }
  return null;
}

/**
 * Advances one authoritative 30 Hz tick. Only the supplied serializable state
 * is mutated; input objects are read but never retained or changed.
 */
export function stepPuzzleMatch(
  state: PuzzleMatchState,
  inputsByPlayer: PuzzleInputsByPlayer,
): PuzzleEvent[] {
  upgradePuzzleMatchStateInPlace(state);
  const events: PuzzleEvent[] = [];
  state.tick += 1;

  if (state.phase === "match_end") {
    return events;
  }

  if (state.phase === "round_end") {
    state.phaseTicksRemaining = Math.max(0, state.phaseTicksRemaining - 1);
    if (state.phaseTicksRemaining === 0) {
      startNextRound(state);
    }
    return events;
  }

  if (state.phase === "countdown") {
    state.phaseTicksRemaining = Math.max(0, state.phaseTicksRemaining - 1);
    if (state.phaseTicksRemaining === 0) {
      state.phase = "playing";
      events.push({ type: "round_started", tick: state.tick, round: state.round });
    }
    return events;
  }

  for (const player of state.players) {
    const board = state.boards[player.id];
    if (board === undefined) {
      continue;
    }
    const input = normalizeInput(inputsByPlayer[player.id], board.aim);
    board.aim = input.aim;
    const heldThisTick =
      input.hold === true &&
      board.lastInput.hold !== true &&
      holdLauncherBubble(state, board, events);
    if (
      !heldThisTick &&
      board.status === "active" &&
      board.activeShot === null &&
      input.fire
    ) {
      fireShot(state, board, events);
    }
    board.lastInput = cloneInput(input);
  }

  const impacts: ShotImpact[] = [];
  for (const player of state.players) {
    const board = state.boards[player.id];
    if (board === undefined || board.status !== "active") {
      continue;
    }
    const impact = updateActiveShot(state, board, events);
    if (impact !== null) {
      impacts.push(impact);
    }
  }

  // Snapshot attack pressure before any same-tick resolution can enqueue more.
  // This makes simultaneous shots independent of player array iteration order.
  const attackPressureToApply: Record<
    string,
    { rows: number; bubbles: number }
  > = {};
  for (const impact of impacts) {
    const board = state.boards[impact.playerId];
    if (board === undefined) {
      continue;
    }
    attackPressureToApply[impact.playerId] = {
      rows: board.pendingPressureRows,
      bubbles: board.pendingAttackBubbles,
    };
    board.pendingPressureRows = 0;
    board.pendingAttackBubbles = 0;
  }

  const resolutions = impacts.map((impact) =>
    resolveShot(state, impact, events),
  );
  for (const resolution of resolutions) {
    const board = state.boards[resolution.playerId];
    if (board === undefined) {
      continue;
    }
    if (resolution.blocked) {
      markBoardLost(state, board, "blocked", events);
      continue;
    }
    if (resolution.cleared) {
      reportBoardCleared(state, board, events);
    }

    const queuedAttack = attackPressureToApply[resolution.playerId] ?? {
      rows: 0,
      bubbles: 0,
    };
    applyBottomAttack(
      state,
      board,
      queuedAttack.rows,
      queuedAttack.bubbles,
      events,
    );
    applyPressureRows(
      state,
      board,
      resolution.missPressureRows,
      "miss",
      events,
    );
  }

  for (const board of Object.values(state.boards)) {
    if (board.status === "active" && boardCrossesDanger(board)) {
      markBoardLost(state, board, "danger", events);
    }
  }

  let winner = statusWinner(state);
  if (winner !== null) {
    finishRound(state, winner, events);
    return events;
  }

  if (state.overtime) {
    winner = updateOvertime(state, events);
    if (winner !== null) {
      finishRound(state, winner, events);
    }
    return events;
  }

  state.roundTicksRemaining = Math.max(0, state.roundTicksRemaining - 1);
  if (state.roundTicksRemaining === 0) {
    beginOvertime(state, events);
  }
  return events;
}
